/* ==========================================================================
   Charliee Product Card - vanilla JS controller
   Carousel (snap + drag) | Wishlist | AJAX cart with ADD <-> stepper morph

   CART PERFORMANCE NOTE

   The stepper used to do this on every single tap:

       POST /cart/change.js   ->   GET /cart.js   ->   GET cart-icon-bubble
       ...and only THEN update the number on screen.

   Three sequential round-trips before the customer saw anything move, with
   the stepper wearing an is-loading class throughout. That is the ~1s per tap.

   It now works the other way round:

     - the number changes INSTANTLY, from local state
     - the network call is debounced 450ms, so five quick taps send ONE request
     - /cart/update.js returns the whole cart, so the separate /cart.js sync
       is gone (one round-trip instead of two)
     - the header bubble refresh is debounced separately and never blocks
     - calls are chained, so a burst of taps can never land out of order
   ========================================================================== */
(function () {
  'use strict';

  var WISHLIST_KEY = 'charliee:wishlist';
  var COMMIT_DELAY = 450;
  var cart = Object.create(null);
  var cartReady = null;

  function api(url, body) {
    var opts = { headers: { Accept: 'application/json' } };
    if (body) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      return res.json();
    });
  }

  function applyCart(data) {
    cart = Object.create(null);
    (data.items || []).forEach(function (item) {
      cart[String(item.variant_id)] = { qty: item.quantity, key: item.key };
    });
    return cart;
  }

  function syncCart() {
    cartReady = api('/cart.js').then(applyCart).catch(function () {
      cart = Object.create(null);
      return cart;
    });
    return cartReady;
  }

  /* header bubble - debounced, never blocks a tap */
  var bubbleTimer = null;
  function refreshCartUI() {
    clearTimeout(bubbleTimer);
    bubbleTimer = setTimeout(function () {
      fetch('/?section_id=cart-icon-bubble')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var next = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-count-bubble');
          document.querySelectorAll('#cart-icon-bubble, .header__icon--cart').forEach(function (host) {
            var prev = host.querySelector('.cart-count-bubble');
            if (prev) prev.remove();
            if (next) host.appendChild(next.cloneNode(true));
          });
        })
        .catch(function () {});
      document.dispatchEvent(new CustomEvent('charliee:cart:updated', { bubbles: true }));
    }, 250);
  }

  /* ------------------------------ carousel ------------------------------ */
  function initCarousel(card) {
    var viewport = card.querySelector('[data-cpc-viewport]');
    if (!viewport || viewport.dataset.cpcReady === '1') return;
    viewport.dataset.cpcReady = '1';

    var dots = Array.prototype.slice.call(card.querySelectorAll('[data-cpc-dot]'));

    function paint() {
      if (!dots.length) return;
      var i = Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth));
      i = Math.min(Math.max(i, 0), dots.length - 1);
      dots.forEach(function (dot, n) {
        dot.classList.toggle('is-active', n === i);
        dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
    }

    var queued = false;
    viewport.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { paint(); queued = false; });
    }, { passive: true });

    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () {
        viewport.scrollTo({ left: viewport.clientWidth * n, behavior: 'smooth' });
      });
    });

    var dragging = false, startX = 0, startScroll = 0, moved = 0;
    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      dragging = true; moved = 0; startX = e.clientX; startScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var d = e.clientX - startX;
      moved = Math.abs(d);
      viewport.scrollLeft = startScroll - d;
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      var w = viewport.clientWidth;
      viewport.scrollTo({ left: Math.round(viewport.scrollLeft / w) * w, behavior: 'smooth' });
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    viewport.addEventListener('click', function (e) {
      if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
      moved = 0;
    }, true);

    paint();
  }

  function initImages(card) {
    card.querySelectorAll('img[data-cpc-img]').forEach(function (img) {
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
      else img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
    });
  }

  /* ------------------------------ wishlist ------------------------------ */
  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (e) { return []; }
  }

  function initWishlist(card) {
    var btn = card.querySelector('[data-cpc-wish]');
    if (!btn || btn.dataset.cpcReady === '1') return;
    btn.dataset.cpcReady = '1';

    var handle = btn.getAttribute('data-handle');
    if (!handle) return;

    function paint(on) {
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    paint(readWishlist().indexOf(handle) !== -1);

    btn.addEventListener('click', function () {
      var list = readWishlist();
      var at = list.indexOf(handle);
      if (at === -1) list.push(handle); else list.splice(at, 1);
      try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) {}
      paint(at === -1);
      document.dispatchEvent(new CustomEvent('charliee:wishlist:updated', {
        bubbles: true, detail: { handle: handle, added: at === -1 }
      }));
    });
  }

  /* ------------------------------ cart ------------------------------ */
  function initCart(card) {
    var host = card.querySelector('[data-cpc-cart]');
    if (!host || host.dataset.cpcReady === '1') return;

    var addBtn = host.querySelector('[data-cpc-add]');
    var stepper = host.querySelector('[data-cpc-stepper]');
    if (!addBtn || !stepper) return;
    host.dataset.cpcReady = '1';

    var variantId = String(addBtn.getAttribute('data-variant') || '');
    var qtyEl = stepper.querySelector('[data-cpc-qty]');
    var minus = stepper.querySelector('[data-cpc-minus]');
    var plus = stepper.querySelector('[data-cpc-plus]');
    if (!variantId || !qtyEl) return;

    var localQty = null;   // what the customer currently sees
    var timer = null;
    var chain = Promise.resolve();

    function showStepper(qty) {
      qtyEl.textContent = qty;
      stepper.classList.remove('cpc-is-off');
      stepper.setAttribute('aria-hidden', 'false');
      addBtn.classList.add('cpc-is-off');
      addBtn.setAttribute('aria-hidden', 'true');
    }

    function showAdd() {
      addBtn.classList.remove('cpc-is-off');
      addBtn.setAttribute('aria-hidden', 'false');
      stepper.classList.add('cpc-is-off');
      stepper.setAttribute('aria-hidden', 'true');
    }

    function paint(qty) {
      if (qty > 0) showStepper(qty); else showAdd();
    }

    /* server state - ignored while the customer has unsent taps */
    function hydrate() {
      if (localQty !== null) return;
      var entry = cart[variantId];
      paint(entry ? entry.qty : 0);
    }

    function commit() {
      var target = localQty;
      chain = chain
        .then(function () {
          var entry = cart[variantId];
          if (!entry) {
            if (target <= 0) return null;
            return api('/cart/add.js', { items: [{ id: Number(variantId), quantity: target }] })
              .then(function () { return api('/cart.js'); })
              .then(applyCart);
          }
          var updates = {};
          updates[variantId] = target;
          return api('/cart/update.js', { updates: updates }).then(applyCart);
        })
        .then(function () {
          // only release local control once nothing further is queued
          if (localQty === target) localQty = null;
          refreshCartUI();
        })
        .catch(function () {
          localQty = null;
          hydrate();
        });
    }

    function bump(delta) {
      var current = localQty;
      if (current === null) {
        var entry = cart[variantId];
        current = entry ? entry.qty : 0;
      }
      var next = current + delta;
      if (next < 0) next = 0;
      if (next === current) return;

      localQty = next;
      paint(next);                       // instant
      clearTimeout(timer);
      timer = setTimeout(commit, COMMIT_DELAY);
    }

    minus.addEventListener('click', function (e) { e.preventDefault(); bump(-1); });
    plus.addEventListener('click', function (e) { e.preventDefault(); bump(1); });

    addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      bump(1);
    });

    /* leaving with taps still queued - send them */
    window.addEventListener('pagehide', function () {
      if (localQty === null) return;
      var entry = cart[variantId];
      if (!entry) return;
      var updates = {};
      updates[variantId] = localQty;
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/cart/update.js',
          new Blob([JSON.stringify({ updates: updates })], { type: 'application/json' }));
      }
    });

    (cartReady || syncCart()).then(hydrate);
    document.addEventListener('charliee:cart:updated', hydrate);
  }

  /* ------------------------------ boot ------------------------------ */
  function mount(root) {
    (root || document).querySelectorAll('[data-cpc-card]').forEach(function (card) {
      initImages(card);
      initCarousel(card);
      initWishlist(card);
      initCart(card);
    });
  }

  function initTrackDrag() {
    document.querySelectorAll('[data-cpc-track]').forEach(function (track) {
      if (track.dataset.cpcReady === '1') return;
      track.dataset.cpcReady = '1';

      var down = false, startX = 0, startScroll = 0, moved = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.target.closest('button, [data-cpc-viewport]')) return;
        down = true; moved = 0; startX = e.clientX; startScroll = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var d = e.clientX - startX;
        moved = Math.abs(d);
        track.scrollLeft = startScroll - d;
      });
      function up() { down = false; track.classList.remove('is-dragging'); }
      track.addEventListener('pointerup', up);
      track.addEventListener('pointerleave', up);
      track.addEventListener('pointercancel', up);
      track.addEventListener('click', function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
        moved = 0;
      }, true);
    });
  }

  function boot() {
    syncCart().then(function () { mount(document); });
    initTrackDrag();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    syncCart().then(function () { mount(e.target || document); });
    initTrackDrag();
  });

  window.CharlieeProductCard = { mount: mount, sync: syncCart };
})();
